import { Alert, Button, Prompt, Tab, Tabs, TextTooltip } from '@components'
import { SubscriptionContent, useBusinessContext } from '@context'
import { useQueryParam } from '@hooks'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { NOCFO_PLAN_OPTIONS, NocfoPlan, NocfoPlanSource } from '@constants'
import { PlanManagementPanel } from '@pages/SettingsPage/constants'
import imgSrc from '@assets/undraw/undraw_upgrade_re_gano.svg'
import ReactMarkdown from 'react-markdown'
import { PlanList } from '@root/containers/Stripe/PlanList'
import { ReceiptTable } from '@root/containers/Stripe/ReceiptTable/ReceiptTable'
import { CancelPlanButton } from '@root/containers/Stripe/CancelPlanButton/CancelPlanButton'
import { StripeUnpaidBillingPanel } from '@root/containers/Stripe/UnpaidInvoicesPanel'
import { PaygUsageSection } from '@root/containers/Stripe/PaygUsageSection/PaygUsageSection'
import { AddonsSection } from '@root/containers/Stripe/AddonsSection'
import { PaymentMethodSection } from '@root/containers/Stripe/PaymentMethods'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { endTrialSubscription, useTrialLimits } from '@root/query'
import { FaCheckCircle, FaQuestionCircle } from 'react-icons/fa'
import { SettingsSubPageLayout } from '@pages/SettingsPage/components/SettingsSubPageLayout'
import { getOptionLabelByValue } from '@utils'

export const PlanSettings: React.FC = () => {
  const [t] = useTranslation()
  const [showEndTrialPrompt, setShowEndTrialPrompt] = useState(false)
  const [panel, setPanel] = useQueryParam('panel')

  const {
    slug,
    data: {
      stripe_subscription_id: stripeSubscriptionId,
      stripe_plan_status: stripePlanStatus,
      subscription_plan: subscriptionPlan,
      subscription_source: subscriptionPlanSource,
      stripe_payg_checkout_enabled: stripePaygCheckoutEnabled,
      billing_lock_reason: billingLockReason
    }
  } = useBusinessContext()
  const hasStripeSubscription = !!stripeSubscriptionId
  const isPaidStripePlan =
    subscriptionPlanSource === NocfoPlanSource.STRIPE &&
    [NocfoPlan.PLUS, NocfoPlan.PRO].includes(subscriptionPlan)
  const isFreePlan = subscriptionPlan === NocfoPlan.FREE
  const isPaygCheckoutFreePlan = stripePaygCheckoutEnabled && isFreePlan
  const isTrialing = stripePlanStatus === 'trialing'
  const canManageStripeSubscription =
    subscriptionPlanSource === NocfoPlanSource.STRIPE &&
    hasStripeSubscription &&
    !isPaygCheckoutFreePlan
  const isManualPlan = subscriptionPlanSource === NocfoPlanSource.MANUAL
  const isAgencyCustomer = subscriptionPlanSource === NocfoPlanSource.AGENCY
  const isNonDirectBillingCustomer = isManualPlan || isAgencyCustomer
  const isManualPaidPlan = isManualPlan && subscriptionPlan !== NocfoPlan.FREE
  const showPaygUsage = stripePaygCheckoutEnabled
  const showAddons = stripePaygCheckoutEnabled
  const showPaygCheckoutBillingPanels =
    stripePaygCheckoutEnabled && hasStripeSubscription && !isNonDirectBillingCustomer
  const showPaymentMethodTab = stripePaygCheckoutEnabled && !isNonDirectBillingCustomer
  const showSubscriptionTab =
    !isNonDirectBillingCustomer &&
    (canManageStripeSubscription ||
      (stripePaygCheckoutEnabled &&
        (!isPaygCheckoutFreePlan ||
          isTrialing ||
          billingLockReason === 'stripe_unpaid' ||
          showPaygUsage ||
          showAddons)))
  const showManagementTabs = showSubscriptionTab || showPaygCheckoutBillingPanels
  const isFreeStripePlan = subscriptionPlanSource === NocfoPlanSource.STRIPE && isFreePlan
  const currentPlanName = getOptionLabelByValue(NOCFO_PLAN_OPTIONS, subscriptionPlan, { t })
  const queryClient = useQueryClient()
  const canChangeStripeSubscription = isPaidStripePlan || isTrialing
  const { data: trialLimits } = useTrialLimits({ enabled: isTrialing })
  const documentUsage = trialLimits?.document_usage
  const hasTrialDocumentLimit = documentUsage?.limit != null
  const trialLimit = documentUsage?.limit ?? 0
  const trialDaysRemaining = trialLimits?.trial_days_remaining ?? 0
  const trialRemaining = documentUsage?.remaining ?? 0
  const paygInvoiceUsage = trialLimits?.payg_invoice_usage
  const hasPaygInvoiceLimit = Boolean(
    paygInvoiceUsage?.is_enabled && paygInvoiceUsage.limit != null
  )
  const paygInvoiceLimit = paygInvoiceUsage?.limit ?? 0
  const paygInvoicesRemaining = paygInvoiceUsage?.remaining ?? paygInvoiceUsage?.limit ?? 0
  const trialLimitReached =
    isTrialing &&
    (Boolean(hasTrialDocumentLimit && trialRemaining <= 0) ||
      Boolean(hasPaygInvoiceLimit && paygInvoicesRemaining <= 0))

  useEffect(() => {
    if (trialLimitReached && panel === PlanManagementPanel.LATEST_CHARGES) {
      setPanel(PlanManagementPanel.SUBSCRIPTION)
    }
  }, [panel, setPanel, trialLimitReached])

  const { mutateAsync: endTrial, isPending: isEndingTrial } = useMutation({
    mutationFn: () => endTrialSubscription({ slug }),
    onSuccess: async () => {
      await queryClient.invalidateQueries()
      setShowEndTrialPrompt(false)
    }
  })
  const isStripeBillingLocked = billingLockReason === 'stripe_unpaid'

  return (
    <SettingsSubPageLayout title={t('settings.billing.managementTitle')} headerContentInset="md">
      <Prompt
        isVisible={showEndTrialPrompt}
        onClose={() => setShowEndTrialPrompt(false)}
        title={t('settings.billing.trialNotice.endTrialPromptTitle', {
          planName: currentPlanName
        })}
        description={
          <>
            <p>
              {t('settings.billing.trialNotice.endTrialPromptDescription', {
                planName: currentPlanName
              })}
            </p>
          </>
        }
        buttons={[
          {
            text: t('general.cancel'),
            action: () => setShowEndTrialPrompt(false)
          },
          {
            text: t('settings.billing.trialNotice.cta'),
            intent: 'primary',
            icon: <FaCheckCircle />,
            disabled: isEndingTrial,
            showSpinner: isEndingTrial,
            action: () => endTrial()
          }
        ]}
      />

      <SubscriptionContent>
        {isManualPaidPlan && (
          <InfoBanner>
            <InfoBannerImageWrapper>
              <img src={imgSrc} alt={t('settings.billing.manualPlanAlt')} />
            </InfoBannerImageWrapper>
            <InfoBannerContent>
              <h1>
                {t('settings.billing.manualSubscriptionTitle', {
                  planName: currentPlanName
                })}
              </h1>
              <p>
                <ReactMarkdown>
                  {t('settings.billing.manualSubscriptionDescription', {
                    planName: currentPlanName
                  })}
                </ReactMarkdown>
              </p>
            </InfoBannerContent>
          </InfoBanner>
        )}

        {isFreeStripePlan && (
          <PlanSummaryCard>
            <h3>
              {t('settings.billing.freePlanSummaryTitle', {
                planName: currentPlanName
              })}
            </h3>
            <p>{t('settings.billing.freePlanSummaryDescription')}</p>
          </PlanSummaryCard>
        )}

        {showManagementTabs && (
          <Tabs queryKey="panel">
            {showSubscriptionTab ? (
              <Tab
                key={PlanManagementPanel.SUBSCRIPTION}
                name={t('settings.billing.planSectionTitle')}
              >
                <SubscriptionTabContent>
                  {isStripeBillingLocked && (
                    <UnpaidBillingPanelCard>
                      <StripeUnpaidBillingPanel />
                    </UnpaidBillingPanelCard>
                  )}

                  {isTrialing && (
                    <>
                      {trialLimitReached ? (
                        <Alert
                          type="info"
                          title={t('settings.billing.trialNotice.limitReachedTitle')}
                          description={
                            <TrialLimitReachedContent>
                              <p>
                                {t(
                                  hasPaygInvoiceLimit
                                    ? 'settings.billing.trialNotice.limitReachedDetailsWithInvoices'
                                    : 'settings.billing.trialNotice.limitReachedDetails',
                                  {
                                    daysLeft: trialDaysRemaining,
                                    documentLimit: trialLimit,
                                    documentsLeft: trialRemaining,
                                    invoiceLimit: paygInvoiceLimit,
                                    invoicesLeft: paygInvoicesRemaining,
                                    planName: currentPlanName
                                  }
                                )}
                              </p>
                              <p>
                                {t('settings.billing.trialNotice.limitReachedDescription', {
                                  planName: currentPlanName
                                })}
                              </p>
                              <Button
                                size="sm"
                                intent="primary"
                                icon={<FaCheckCircle />}
                                showSpinner={isEndingTrial}
                                onClick={() => setShowEndTrialPrompt(true)}
                              >
                                {t('settings.billing.trialNotice.cta')}
                              </Button>
                            </TrialLimitReachedContent>
                          }
                        />
                      ) : (
                        <Alert
                          type="info"
                          title={t('settings.billing.trialNotice.title')}
                          description={
                            <TrialNoticeContent>
                              <p>
                                {hasTrialDocumentLimit
                                  ? t(
                                      hasPaygInvoiceLimit
                                        ? 'settings.billing.trialNotice.descriptionWithInvoices'
                                        : 'settings.billing.trialNotice.description1',
                                      {
                                        daysLeft: trialDaysRemaining,
                                        documentsLeft: trialRemaining,
                                        invoicesLeft: paygInvoicesRemaining,
                                        planName: currentPlanName
                                      }
                                    )
                                  : t('settings.billing.trialNotice.descriptionWithoutLimits', {
                                      planName: currentPlanName
                                    })}
                              </p>
                              <p>{t('settings.billing.trialNotice.description2')}</p>
                              <Button
                                size="sm"
                                intent="primary"
                                showSpinner={isEndingTrial}
                                onClick={() => setShowEndTrialPrompt(true)}
                              >
                                {t('settings.billing.trialNotice.cta')}
                              </Button>
                            </TrialNoticeContent>
                          }
                        />
                      )}
                    </>
                  )}
                  {!isStripeBillingLocked && (
                    <SubscriptionPanel>
                      {canChangeStripeSubscription && (
                        <SubscriptionBlock>
                          <BlockTitle>{t('settings.billing.updateYourSubscription')}</BlockTitle>
                          <PlanList />
                        </SubscriptionBlock>
                      )}
                      {showAddons && <AddonsSection />}
                      {showPaygUsage && (
                        <SubscriptionBlock>
                          <BlockTitle>
                            <span>
                              {isTrialing
                                ? t('settings.billing.paygUsage.trial.title')
                                : t('settings.billing.paygUsage.title')}
                            </span>
                            <TextTooltip
                              placement="bottom-start"
                              maxWidth={360}
                              tooltip={
                                isTrialing
                                  ? t('settings.billing.paygUsage.trial.description')
                                  : t('settings.billing.paygUsage.description')
                              }
                            >
                              <HelpIcon
                                aria-label={
                                  isTrialing
                                    ? t('settings.billing.paygUsage.trial.description')
                                    : t('settings.billing.paygUsage.description')
                                }
                              />
                            </TextTooltip>
                          </BlockTitle>
                          <PaygUsageSection />
                        </SubscriptionBlock>
                      )}
                    </SubscriptionPanel>
                  )}
                </SubscriptionTabContent>
              </Tab>
            ) : null}

            {canManageStripeSubscription || showPaygCheckoutBillingPanels ? (
              <Tab
                key={PlanManagementPanel.LATEST_CHARGES}
                name={t('settings.billing.latestCharges')}
              >
                <ReceiptTable />
              </Tab>
            ) : null}

            {showPaymentMethodTab ? (
              <Tab
                key={PlanManagementPanel.PAYMENT_METHOD}
                name={t('settings.billing.paymentAndBillingTitle')}
              >
                <PaymentMethodSection />
              </Tab>
            ) : null}

            {canManageStripeSubscription &&
            canChangeStripeSubscription &&
            stripePaygCheckoutEnabled ? (
              <Tab
                key={PlanManagementPanel.CANCEL_SUBSCRIPTION}
                name={t('settings.billing.cancelSubscriptionTitle')}
              >
                <CancelPlanButton />
              </Tab>
            ) : null}
          </Tabs>
        )}
      </SubscriptionContent>
    </SettingsSubPageLayout>
  )
}

const TrialNoticeContent = styled.div`
  display: flex;
  flex-direction: column;

  button {
    align-self: flex-start;
  }
`

const TrialLimitReachedContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;

  h4 {
    margin: 0;
    font-size: ${({ theme }) => theme.fontSize.md}rem;
    color: ${({ theme }) => theme.colors.neutralBlack};
  }

  p {
    margin: 0;
  }

  button {
    align-self: flex-start;
  }
`

const InfoBanner = styled.div`
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.neutralGray};
  padding: ${({ theme }) => theme.spacing.xxl}rem;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xxl}rem;
`

const InfoBannerImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;

  img {
    max-width: 300px;
  }
`

const InfoBannerContent = styled.div`
  flex: 100;
  min-width: 50%;
  color: ${({ theme }) => theme.colors.neutralBlack};

  h1 {
    color: ${({ theme }) => theme.colors.primary};
    font-size: ${({ theme }) => theme.fontSize.lg}rem;
  }
`

const PlanSummaryCard = styled.div`
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.neutralGray};
  padding: ${({ theme }) => theme.spacing.xl}rem;
  margin-bottom: ${({ theme }) => theme.spacing.md}rem;
  color: ${({ theme }) => theme.colors.neutralBlack};

  h3 {
    color: ${({ theme }) => theme.colors.primary};
    font-size: ${({ theme }) => theme.fontSize.lg}rem;
    margin-top: 0;
    margin-bottom: ${({ theme }) => theme.spacing.sm}rem;
  }

  p {
    margin: 0;
  }
`

const UnpaidBillingPanelCard = styled.div`
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
  padding: ${({ theme }) => theme.spacing.xl}rem;
  margin-bottom: ${({ theme }) => theme.spacing.md}rem;
  border: 1px solid ${({ theme }) => theme.colors.warning}40;
`

const SubscriptionTabContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const SubscriptionPanel = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xl}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
`

const SubscriptionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const BlockTitle = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.neutralBlack};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const HelpIcon = styled(FaQuestionCircle)`
  display: block;
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  cursor: pointer;
`
